import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/config/storageDatabase';

type RelayRecords = Record<string, unknown>;

type GraphQLRequest = {
  text?: string | null;
};

type GraphQLJsonResponse<T = any> = { data?: T; errors?: any[] };

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const handleAuthError = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    window.location.href = '/login';
  }
};

const hasAuthError = (json: any): boolean => {
  const errors = json?.errors;
  if (!errors || !Array.isArray(errors)) return false;
  return errors.some(
    (error) => error?.extensions?.code === 'AUTH_REQUIRED'
  );
};

export const fetchGraphQL = async <T = any>(
  params: GraphQLRequest,
  variables?: Record<string, any>
): Promise<GraphQLJsonResponse<T>> => {
  const endpoint = process.env.GRAPHQL_ENDPOINT;
  if (!endpoint) throw new Error('GRAPHQL_ENDPOINT is not defined');
  const token = typeof window !== 'undefined' ? localStorage.getItem(ACCESS_TOKEN_KEY) : null;
  const maxRetries = 5;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ query: params.text, variables })
      });

      const json = await response.json();

      if (hasAuthError(json)) {
        handleAuthError();
      }

      return json;
    } catch (error) {
      if (attempt < maxRetries && (error as any)?.cause?.code === 'ECONNREFUSED') {
        await delay(1000 * (attempt + 1));
        continue;
      }
      throw error;
    }
  }
  throw new Error('fetchGraphQL: retries exhausted');
};

export type { RelayRecords };
